import { ContextMenu, Tooltip } from "radix-ui";
import { useEffect, useRef, type KeyboardEvent, type ReactNode, type Ref } from "react";
import { MenuEntries } from "./MenuEntries.tsx";
import { cx, radius as radiusVar, type RadiusToken } from "./tokens.ts";
import type { MenuEntry } from "./types.ts";

export type PressableProps = {
  onPress: () => void;
  onLongPress?: () => void;
  /** Long-press / right-click menu (items, toggles with a checkmark, separators). */
  contextMenu?: MenuEntry[];
  /** The item that is open now (sidebar row of the open channel, current tab): `aria-current="page"`, selected background. */
  current?: boolean;
  /**
   * The keyboard highlight of a listbox (autocomplete, slash menu, picker list driven by arrow keys): `role="option"` +
   * `aria-selected`, the shared highlight colour. Give the container `role="listbox"` and point the field's
   * `aria-activedescendant` at the highlighted row's `id`.
   */
  highlighted?: boolean;
  /** A toggle button's state (my reaction, a formatting toggle): `aria-pressed`, selected background. */
  pressed?: boolean;
  /**
   * @deprecated Use `current`, `highlighted` or `pressed`. Kept for existing callers: selected background +
   * `aria-pressed`, exactly as before.
   */
  selected?: boolean;
  disabled?: boolean;
  accessibilityLabel?: string;
  /** Shape of the hover/pressed/selected highlight (default "sm"). */
  radius?: RadiusToken;
  /** Lay children out horizontally (row) instead of vertically. */
  direction?: "vertical" | "horizontal";
  /** Scroll into view (nearest edge) when it becomes selected: keyboard-driven menus. */
  revealWhenSelected?: boolean;
  /** The pointer moved onto it (menus whose highlighted row follows the mouse; no-op on touch). */
  onHover?: () => void;
  /** Hover / keyboard-focus tooltip (e.g. who reacted). Web: a styled tooltip instead of the native title. */
  tooltip?: string;
  /** No hover / pressed tint of its own: a row whose container (`Stack hoverHighlight`) tints as a whole (mail rows). */
  bare?: boolean;
  /**
   * Feedback for the user's own action (the reaction chip they just added): whenever this is set or changes, it plays
   * a short pop and scrolls into view (nearest edge, smooth) if it isn't fully visible. Respects reduced motion.
   */
  attentionKey?: string | number;
  testID: string;
  children?: ReactNode;
};

const LONG_PRESS_MS = 500;
/** Room kept between a revealed pressable and the edge of its scroller. */
const REVEAL_MARGIN = 12;

/** Scrolls the nearest scrolling ancestor just enough to show `el`; nothing moves when it is already fully visible. */
function revealNearest(el: HTMLElement, instant: boolean): void {
  let box = el.parentElement;
  while (box !== null) {
    const overflow = getComputedStyle(box).overflowY;
    if ((overflow === "auto" || overflow === "scroll") && box.scrollHeight > box.clientHeight) break;
    box = box.parentElement;
  }
  if (box === null) return;
  const r = el.getBoundingClientRect();
  const c = box.getBoundingClientRect();
  const below = r.bottom + REVEAL_MARGIN - c.bottom;
  const above = c.top - (r.top - REVEAL_MARGIN);
  const delta = below > 0 ? below : above > 0 ? -above : 0;
  if (delta !== 0) box.scrollBy({ top: delta, behavior: instant ? "auto" : "smooth" });
}

export function Pressable(p: PressableProps & { ref?: Ref<HTMLDivElement> } & Record<string, unknown>) {
  const { onPress, onLongPress, contextMenu, current, highlighted, pressed, selected, disabled, accessibilityLabel, radius, direction, revealWhenSelected, onHover, tooltip, bare, attentionKey, testID, children, ref, ...rest } = p;
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const own = useRef<HTMLDivElement | null>(null);
  const active = highlighted === true || current === true || selected === true;
  useEffect(() => {
    if (revealWhenSelected === true && active) own.current?.scrollIntoView({ block: "nearest" });
  }, [revealWhenSelected, active]);
  const toggled = pressed ?? selected;
  useEffect(() => {
    const node = own.current;
    if (attentionKey === undefined || node === null) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
    revealNearest(node, reduce);
    // Restart the CSS pop (primitives.css, [data-attention]); React never renders this attribute.
    node.removeAttribute("data-attention");
    void node.offsetWidth;
    node.setAttribute("data-attention", "");
    const done = () => node.removeAttribute("data-attention");
    node.addEventListener("animationend", done, { once: true });
    return () => node.removeEventListener("animationend", done);
  }, [attentionKey]);
  const longFired = useRef(false);
  const clear = () => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = undefined;
  };
  // A Radix trigger (`Menu`, `Popover`, `Tooltip` with asChild) hands its own handlers down as props: run them first,
  // and skip ours when they handled the event, so a Pressable can be a Menu trigger.
  const theirs = rest as Partial<Record<"onClick" | "onKeyDown" | "onPointerDown" | "onPointerUp" | "onPointerLeave" | "onPointerEnter", (e: never) => void>>;
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    theirs.onKeyDown?.(e as never);
    if (disabled === true || e.defaultPrevented) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onPress();
    }
  };
  const el = (
    <div
      {...rest}
      ref={(node) => {
        own.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref !== undefined && ref !== null) (ref as { current: HTMLDivElement | null }).current = node;
      }}
      role={highlighted === undefined ? "button" : "option"}
      // Listbox options keep the keyboard in their field (active descendant), so they are not tab stops.
      tabIndex={disabled === true || highlighted !== undefined ? -1 : 0}
      aria-disabled={disabled === true ? true : undefined}
      aria-pressed={toggled === undefined || highlighted !== undefined ? undefined : toggled}
      aria-selected={highlighted === undefined ? undefined : highlighted}
      aria-current={current === true ? "page" : undefined}
      aria-label={accessibilityLabel}
      title={tooltip === undefined ? accessibilityLabel : undefined}
      data-testid={testID}
      className={cx(
        "b-pressable",
        direction === "horizontal" && "b-pressable-h",
        bare === true && "b-pressable-bare",
        (current === true || toggled === true) && "b-selected",
        highlighted === true && "b-highlighted",
        disabled === true && "b-disabled",
      )}
      style={radius === undefined ? undefined : { borderRadius: radiusVar(radius) }}
      onClick={(e) => {
        theirs.onClick?.(e as never);
        if (disabled === true || longFired.current) {
          longFired.current = false;
          return;
        }
        e.stopPropagation();
        onPress();
      }}
      onKeyDown={onKeyDown}
      onPointerDown={(e) => {
        theirs.onPointerDown?.(e as never);
        if (onLongPress === undefined || disabled === true) return;
        longFired.current = false;
        timer.current = setTimeout(() => {
          longFired.current = true;
          onLongPress();
        }, LONG_PRESS_MS);
      }}
      onPointerEnter={(e) => {
        theirs.onPointerEnter?.(e as never);
        if (onHover !== undefined && e.pointerType !== "touch" && disabled !== true) onHover();
      }}
      onPointerUp={(e) => {
        theirs.onPointerUp?.(e as never);
        clear();
      }}
      onPointerLeave={(e) => {
        theirs.onPointerLeave?.(e as never);
        clear();
      }}
    >
      {children}
    </div>
  );
  const hasMenu = contextMenu !== undefined && contextMenu.length > 0;
  const trigger = hasMenu ? <ContextMenu.Trigger asChild>{el}</ContextMenu.Trigger> : el;
  const tipped =
    tooltip === undefined ? (
      trigger
    ) : (
      <Tooltip.Root delayDuration={300}>
        <Tooltip.Trigger asChild>{trigger}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content className="b-tooltip" side="top" sideOffset={6}>
            {tooltip}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  if (!hasMenu) return tipped;
  return (
    <ContextMenu.Root>
      {tipped}
      <ContextMenu.Portal>
        <ContextMenu.Content className="b-menu">
          <MenuEntries kind="context" entries={contextMenu} />
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
